import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ProfileScreenshot, Language } from '../types';
import { ChevronLeft, ChevronRight, ExternalLink, Sparkles, User, Maximize2, X } from 'lucide-react';

interface ProfileScreenshotsCarouselProps {
  screenshots: ProfileScreenshot[];
  language: Language;
}

export const ProfileScreenshotsCarousel: React.FC<ProfileScreenshotsCarouselProps> = ({ screenshots, language }) => {
  const [index, setIndex] = useState(0);
  const [previewModalShot, setPreviewModalShot] = useState<ProfileScreenshot | null>(null);

  useEffect(() => {
    if (screenshots.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % screenshots.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [screenshots.length]);

  if (!screenshots || screenshots.length === 0) return null;

  const getVisibleItems = () => {
    const len = screenshots.length;
    if (len === 1) return [{ item: screenshots[0], position: 'center' as const }];
    const prevIndex = (index - 1 + len) % len;
    const nextIndex = (index + 1) % len;
    return [
      { item: screenshots[prevIndex], position: 'left' as const },
      { item: screenshots[index], position: 'center' as const },
      { item: screenshots[nextIndex], position: 'right' as const },
    ];
  };

  const handlePrev = () => {
    setIndex((prev) => (prev - 1 + screenshots.length) % screenshots.length);
  };

  const handleNext = () => {
    setIndex((prev) => (prev + 1) % screenshots.length);
  };

  return (
    <div className="relative w-full max-w-[1500px] mx-auto pt-6 pb-16 flex flex-col items-center select-none">
      {/* Header Section */}
      <div className="text-center mb-10 space-y-3">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-950/40 border border-purple-500/30 text-purple-300 text-[11px] font-black uppercase tracking-widest shadow-[0_0_20px_rgba(168,85,247,0.2)]">
          <Sparkles className="w-3.5 h-3.5 text-purple-400 animate-pulse" />
          <span>
            {language === 'en'
              ? 'Steam Profile Setups'
              : language === 'es'
              ? 'Perfiles de Steam de Clientes'
              : language === 'ru'
              ? 'Профили Steam Клиентов'
              : 'Showcase de Perfis Steam'}
          </span>
        </div>
        
        <h2 className="text-2xl md:text-4xl font-black text-white uppercase tracking-tight">
          {language === 'en'
            ? 'Client Profile Showcase'
            : language === 'es'
            ? 'Perfiles Reales de Clientes'
            : language === 'ru'
            ? 'Реальные профили пользователей'
            : 'Perfis de Clientes em Ação'}
        </h2>
        
        <p className="text-zinc-400 text-xs md:text-sm max-w-xl mx-auto font-medium">
          {language === 'en'
            ? 'Check out real Steam profiles configured and decorated with our custom artworks and designs.'
            : language === 'es'
            ? 'Mira perfiles reales de Steam configurados y decorados con nuestras obras de arte y diseños.'
            : language === 'ru'
            ? 'Посмотрите реальные профили Steam, оформленные нашими иллюстрациями и фонами.'
            : 'Veja perfis reais de usuários decorados e personalizados com nossas artes e animações.'}
        </p>
      </div>

      {/* 3D Rotating Carousel Container */}
      <div className="relative w-full h-[380px] md:h-[580px] flex items-center justify-center" style={{ perspective: '1400px' }}>
        <AnimatePresence initial={false} mode="popLayout">
          {getVisibleItems().map(({ item, position }) => {
            const isCenter = position === 'center';
            const isLeft = position === 'left';
            const isRight = position === 'right';

            return (
              <motion.div
                key={item.id}
                initial={{
                  opacity: 0,
                  scale: 0.6,
                  x: isLeft ? -550 : isRight ? 550 : 0,
                  rotateY: isLeft ? 45 : isRight ? -45 : 0,
                  zIndex: 0,
                }}
                animate={{
                  opacity: isCenter ? 1 : 0.35,
                  scale: isCenter ? 1 : 0.75,
                  x: isCenter ? 0 : isLeft ? -420 : 420,
                  rotateY: isCenter ? 0 : isLeft ? 32 : -32,
                  zIndex: isCenter ? 30 : 10,
                }}
                exit={{
                  opacity: 0,
                  scale: 0.6,
                  x: isLeft ? -550 : isRight ? 550 : 0,
                  zIndex: 0,
                }}
                transition={{
                  duration: 0.8,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className={`absolute w-[330px] sm:w-[480px] md:w-[720px] h-[260px] sm:h-[360px] md:h-[500px] flex items-center justify-center ${
                  isCenter ? 'pointer-events-auto cursor-pointer' : 'pointer-events-none'
                }`}
                onClick={() => {
                  if (isCenter) setPreviewModalShot(item);
                }}
              >
                <div className={`relative w-full h-full rounded-2xl overflow-hidden border-2 transition-all duration-700 bg-zinc-950/80 backdrop-blur-md flex flex-col ${
                  isCenter
                    ? 'border-[#c084fc]/80 shadow-[0_0_50px_rgba(168,85,247,0.4)] brightness-105'
                    : 'border-white/10 brightness-50 grayscale-[0.8]'
                }`}>
                  {/* Top Bar simulating browser/profile frame */}
                  <div className="px-4 py-2.5 bg-black/80 border-b border-white/10 flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-500/80"></div>
                      <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80"></div>
                      <div className="w-2.5 h-2.5 rounded-full bg-green-500/80"></div>
                      <span className="text-[10px] font-mono text-zinc-400 truncate max-w-[150px] sm:max-w-[240px] ml-2">
                        {item.title || 'Steam Profile'}
                      </span>
                    </div>

                    <div className="flex items-center gap-3">
                      {item.userName && (
                        <div className="flex items-center gap-1.5 text-[10px] font-bold text-purple-300">
                          <User className="w-3 h-3 text-purple-400" />
                          <span>{item.userName}</span>
                        </div>
                      )}
                      {isCenter && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewModalShot(item);
                          }}
                          className="p-1 hover:bg-white/10 rounded text-zinc-400 hover:text-white transition-colors"
                          title="Ampliar captura"
                        >
                          <Maximize2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Screenshot Image Container */}
                  <div className="relative flex-1 w-full bg-black/90 overflow-hidden flex items-center justify-center group">
                    <img
                      src={item.imageUrl}
                      alt={item.title || 'Steam Profile Screenshot'}
                      className="w-full h-full object-contain md:object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />

                    {/* Gradient Overlay & Details on hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-5">
                      <div className="w-full flex items-center justify-between">
                        <div>
                          <h4 className="text-white font-bold text-sm drop-shadow-md">{item.title || 'Steam Profile'}</h4>
                          {item.userName && <p className="text-purple-300 text-xs font-mono">@{item.userName}</p>}
                        </div>
                        {item.steamUrl && (
                          <a
                            href={item.steamUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="px-3.5 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold flex items-center gap-1.5 shadow-lg transition-all"
                          >
                            <span>Steam</span>
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>

        {/* Carousel Navigation Arrows */}
        {screenshots.length > 1 && (
          <>
            <button
              onClick={handlePrev}
              className="absolute left-2 sm:left-6 md:left-12 z-40 p-3 md:p-4 rounded-full bg-black/60 hover:bg-[#c084fc] border border-purple-500/40 text-white transition-all shadow-[0_0_20px_rgba(0,0,0,0.8)] hover:scale-110 group"
              aria-label="Previous screenshot"
            >
              <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 group-hover:-translate-x-0.5 transition-transform" />
            </button>
            <button
              onClick={handleNext}
              className="absolute right-2 sm:right-6 md:right-12 z-40 p-3 md:p-4 rounded-full bg-black/60 hover:bg-[#c084fc] border border-purple-500/40 text-white transition-all shadow-[0_0_20px_rgba(0,0,0,0.8)] hover:scale-110 group"
              aria-label="Next screenshot"
            >
              <ChevronRight className="w-5 h-5 md:w-6 md:h-6 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </>
        )}
      </div>

      {/* Bottom Dot Indicators */}
      {screenshots.length > 1 && (
        <div className="flex items-center gap-2.5 mt-6 z-20">
          {screenshots.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              className={`h-2 transition-all rounded-full ${
                index === i
                  ? 'w-8 bg-[#c084fc] shadow-[0_0_12px_#c084fc]'
                  : 'w-2 bg-zinc-700 hover:bg-zinc-500'
              }`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}

      {/* Lightbox / Zoom Modal */}
      {previewModalShot && (
        <div className="fixed inset-0 z-[3000] flex items-center justify-center p-4 md:p-8 bg-black/90 backdrop-blur-md animate-in fade-in duration-300">
          <div className="absolute inset-0" onClick={() => setPreviewModalShot(null)} />
          <div className="relative max-w-6xl w-full max-h-[90vh] bg-zinc-950 border-2 border-purple-500/60 rounded-2xl overflow-hidden shadow-[0_0_60px_rgba(168,85,247,0.5)] z-10 flex flex-col">
            <div className="px-6 py-4 bg-zinc-900 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Sparkles className="w-4 h-4 text-purple-400" />
                <h3 className="text-white font-bold text-sm md:text-base">
                  {previewModalShot.title || 'Steam Profile Setup'}
                </h3>
                {previewModalShot.userName && (
                  <span className="text-xs font-mono text-purple-400 bg-purple-950/60 px-2.5 py-0.5 rounded border border-purple-800/40">
                    @{previewModalShot.userName}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                {previewModalShot.steamUrl && (
                  <a
                    href={previewModalShot.steamUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold flex items-center gap-1.5 transition-all"
                  >
                    <span>Ver Perfil Steam</span>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
                <button
                  onClick={() => setPreviewModalShot(null)}
                  className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-full transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-black/95">
              <img
                src={previewModalShot.imageUrl}
                alt={previewModalShot.title || 'Profile Screenshot'}
                className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
